import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { AppCasinoState, CasinoGameModelExt, GameWrapperType } from 'src/domains/casino/state/AppCasino.state';
import { GameImgWrapperLazy, GameWrapper } from './SingleGameExt.style';
import { JackpotBar } from './jackpot-bar/JackpotBar';
import { RealTimeLobby } from './real-time-lobby/RealTimeLobby';
import { ThumbnailOverlay } from './thumbnail-overlay/ThumbnailOverlay';
import { useCommon } from 'src/domains/common/Common';
import { JackpotsSocket } from 'src/domains/casino/state/AppCasino/casino-sockets/jackpot-socket/JackpotsSocket';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

const getImageForWrapper = (
    gameModel: CasinoGameModelExt,
    wrapperType: GameWrapperType,
    isFirstTile: boolean | undefined
): string => {
    if (wrapperType === 'slider-grid' && isFirstTile === true) {
        return gameModel.imageSquare;
    }

    if (wrapperType === 'numeric-trending') {
        return gameModel.imageVertical;
    }

    return gameModel.imageHorizontal;
};

interface PropsType {
    appCasinoState: AppCasinoState;
    gameModel: CasinoGameModelExt;
    wrapperType?: GameWrapperType;
    showModalOnMobile?: boolean;
    showLikeAndInfo?: boolean;
    showRealTimeLobby?: boolean;
    isFirstTile?: boolean;
}

export const SingleGameExt = observer('SingleGameExt', (props: PropsType) => {
    const {
        appCasinoState,
        gameModel,
        isFirstTile,
        showLikeAndInfo,
        showModalOnMobile,
        showRealTimeLobby,
        wrapperType,
    } = props;

    const common = useCommon();
    const configState = ConfigComponents.get(common).config;

    const jackpotEnabled = configState.casinoJackPot;

    const thumbnailConfigSettings = configState.gameThumbnailSettings[gameModel.collection];
    const gameWrapperType = wrapperType ?? thumbnailConfigSettings.wrapperType;
    const isShowModalOnMobile = showModalOnMobile ?? thumbnailConfigSettings.showModalOnMobile;
    const isShowLikeAndInfo = showLikeAndInfo ?? thumbnailConfigSettings.showLikeAndInfo;
    const isShowRealTimeLobby = showRealTimeLobby ?? thumbnailConfigSettings.showRealTimeLobby;

    const imageToDisplay = getImageForWrapper(gameModel, gameWrapperType, isFirstTile);

    const showModal = (): void => {
        appCasinoState.casinoSidebarGameInfoState.showSidebar(gameModel.id);
        appCasinoState.redirectState.resetRealityCheckTime();
    };

    const handleOnMobileClick = (): void => {
        if (isShowModalOnMobile) {
            showModal();
        } else {
            gameModel.startGame();
        }
    };

    const jackpotModel = jackpotEnabled ? JackpotsSocket.get(common).getModel(props.gameModel.id) : null;

    const imgThumbnailSize = isFirstTile === true ? 552 : 360;

    return (
        <GameWrapper
            wrapperType={gameWrapperType}
            title={gameModel.name}
            data-test-game-id={gameModel.id}
        >
            <GameImgWrapperLazy
                alt={gameModel.name}
                imgFit='fill'
                imgBase={{
                    src: imageToDisplay,
                    optimizeOptions: {
                        width: imgThumbnailSize,
                        quality: 85,
                    },
                }}
                onClick={handleOnMobileClick}
                placeholder={true}
            />

            {jackpotEnabled && jackpotModel !== null ? (
                <JackpotBar
                    jackpotModel={jackpotModel}
                    isBigTile={isFirstTile ?? false}
                />
            ) : null}

            <RealTimeLobby
                appCasinoState={appCasinoState}
                gameModel={gameModel}
                showRealTimeLobby={isShowRealTimeLobby}
            />
            <ThumbnailOverlay
                gameModel={gameModel}
                showLikeAndInfo={isShowLikeAndInfo}
                handleOnMobileClick={handleOnMobileClick}
                handleInfoClick={showModal}
            />
        </GameWrapper>
    );
});
